import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";

// --- Types ---

export interface CollectionDashboardKpis {
  activeCount: number;
  activeTotalValue: number;
  promisedCount: number;
  promisedTotalValue: number;
  escalatedCount: number;
  escalatedTotalValue: number;
  onHoldCount: number;
  onHoldTotalValue: number;
  closedThisMonthCount: number;
  closedThisMonthTotalValue: number;
}

// --- GraphQL ---

interface KpiTotalsResponse {
  collectionCases: {
    collectionCaseTotals: {
      totalCount: number;
      totalOutstanding: number;
      totalDisplayOutstanding: number;
    };
  };
}

const KPI_TOTALS_QUERY = `
  query DashboardKpiTotals($filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseTotals(filter: $filter) {
        totalCount
        totalOutstanding
        totalDisplayOutstanding
      }
    }
  }
`;

// --- Helpers ---

async function fetchKpiTotals(
  apiConfig: Parameters<typeof graphqlQuery>[0],
  filter: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ count: number; totalValue: number; displayTotalValue: number }> {
  const response = await graphqlQuery<KpiTotalsResponse>(
    apiConfig,
    KPI_TOTALS_QUERY,
    { filter },
    signal,
  );
  const totals = response.collectionCases.collectionCaseTotals;
  return { count: totals.totalCount, totalValue: totals.totalOutstanding, displayTotalValue: totals.totalDisplayOutstanding };
}

// --- Hook ---

export function useCollectionDashboardKpis(selectedUserId?: string, currencyCode?: string) {
  const apiConfig = useApiConfig();

  const mockCases = getGlobalDataStore().getAllCases();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const mockKpis: CollectionDashboardKpis = {
    activeCount: mockCases.filter(x => x.status === "Active").length,
    activeTotalValue: mockCases.filter(x => x.status === "Active").reduce((s, x) => s + x.amount, 0),
    promisedCount: mockCases.filter(x => x.status === "Promised").length,
    promisedTotalValue: mockCases.filter(x => x.status === "Promised").reduce((s, x) => s + x.amount, 0),
    escalatedCount: mockCases.filter(x => x.status === "Escalated").length,
    escalatedTotalValue: mockCases.filter(x => x.status === "Escalated").reduce((s, x) => s + x.amount, 0),
    onHoldCount: mockCases.filter(x => x.status === "On Hold").length,
    onHoldTotalValue: mockCases.filter(x => x.status === "On Hold").reduce((s, x) => s + x.amount, 0),
    closedThisMonthCount: mockCases.filter(x => x.status === "Closed" && x.updatedAt >= thirtyDaysAgo).length,
    closedThisMonthTotalValue: mockCases.filter(x => x.status === "Closed" && x.updatedAt >= thirtyDaysAgo).reduce((s, x) => s + x.amount, 0),
  };

  return useModeAwareQuery<CollectionDashboardKpis, Error>(
    ["collection-dashboard-kpis", selectedUserId, currencyCode],
    async ({ signal }) => {
      const userFilter = selectedUserId && selectedUserId !== "everyone"
        ? { assignedToUserId: { eq: Number(selectedUserId) } }
        : {};

      const currencyFilter = currencyCode
        ? { currencyCode: { eq: currencyCode } }
        : {};

      const thirtyDaysAgoIso = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const [active, promised, escalated, onHold, closedThisMonth] = await Promise.all([
        fetchKpiTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: "Active" } }, signal),
        fetchKpiTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: "Promised" } }, signal),
        fetchKpiTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: "Escalated" } }, signal),
        fetchKpiTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: "On Hold" } }, signal),
        fetchKpiTotals(apiConfig, { ...userFilter, ...currencyFilter, status: { eq: "Closed" }, updatedAt: { ge: thirtyDaysAgoIso } }, signal),
      ]);

      const useCaseCurrency = !!currencyCode;

      return {
        activeCount: active.count,
        activeTotalValue: useCaseCurrency ? active.displayTotalValue : active.totalValue,
        promisedCount: promised.count,
        promisedTotalValue: useCaseCurrency ? promised.displayTotalValue : promised.totalValue,
        escalatedCount: escalated.count,
        escalatedTotalValue: useCaseCurrency ? escalated.displayTotalValue : escalated.totalValue,
        onHoldCount: onHold.count,
        onHoldTotalValue: useCaseCurrency ? onHold.displayTotalValue : onHold.totalValue,
        closedThisMonthCount: closedThisMonth.count,
        closedThisMonthTotalValue: useCaseCurrency ? closedThisMonth.displayTotalValue : closedThisMonth.totalValue,
      };
    },
    mockKpis,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}
